import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { apiRequestContractHandler } from "shared/api/apiRequestContractHandler";
import { postSocietyProfileAccountingInvoicePaymentContract } from "shared/api/contracts/society/societyId/profiles/profileId/accounting/invoice/invoiceId/payment";
import { AuthorizeAcceptSuite } from "shared/AuthorizeAcceptSuite";
import PillarForm from "shared/components/pillar-form/PillarForm";
import TemplateComponent from "shared/components/template/TemplateComponent";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { defaultAddressFromProfile } from "shared/helpers/profile/address/defaultAddressFromProfile";
import { getDisplayNameHelper } from "shared/helpers/profile/getDisplayNameHelper";
import { INVOICE_STATUS } from "hub/src/appConstants";
import CheckPaymentForm from "hub/src/components/checkout/CheckPaymentForm";
import {
  allInvoicesQueryKey,
  invoicesTableKey,
  unPaidInvoicesQueryKey,
} from "hub/src/components/overview/UserDetail";
import { useHubCheckoutContext } from "hub/src/pages/checkout/context/HubCheckoutContext";
import { HubCheckoutAddressForm } from "hub/src/pages/checkout/payment-form/HubCheckoutAddressForm";
import HubCheckoutPaymentDetailsForm from "hub/src/pages/checkout/payment-form/HubCheckoutPaymentDetailsForm";
import {
  formatCardDetails,
  getProfileIsExpired,
  PaymentType,
} from "hub/src/pages/checkout/payment-form/HubCheckoutPaymentFormHelpers";
import { HubCheckoutPaymentOptions } from "hub/src/pages/checkout/payment-form/HubCheckoutPaymentOptions";
import { HubCheckoutSameAsBillingCheckbox } from "hub/src/pages/checkout/payment-form/HubCheckoutSameAsBillingCheckbox";
import { paymentFormvalidationSchema } from "hub/src/yupValidationSchema/payment-form-schema";

const HubCheckoutPaymentForm = () => {
  const queryClient = useQueryClient();
  const router = useCustomRouter();
  const session = useCustomSession();
  const {
    invoice,
    invoiceIsLoading,
    paymentType,
    routeConfig,
    setCheckoutError,
    payButtonDisabled,
    paymentFormValues,
    setPaymentFormValues,
  } = useHubCheckoutContext();

  const billingAddress = defaultAddressFromProfile({
    profile: session?.profile,
    use: routeConfig.routeParams?.billingAddressUse,
    type: routeConfig.routeParams?.billingAddressType,
  });

  const shippingAddress = defaultAddressFromProfile({
    profile: session?.profile,
    use: routeConfig.routeParams?.shippingAddressUse,
    type: routeConfig.routeParams?.shippingAddressType,
  });

  useEffect(() => {
    const fetchAuthorizeNetScript = async () => {
      const script = document.createElement("script");
      script.src =
        session.society?.societySettingsPublic?.authorizeNetAcceptSuiteUrl ??
        "";
      script.async = true;
      document.body.appendChild(script);
    };

    fetchAuthorizeNetScript();
  }, []);

  const handleSubmit = async () => {
    const formattedCardDetails = formatCardDetails({
      cardNumber: paymentFormValues!.cardNumber!,
      expirationDate: paymentFormValues!.expirationDate!,
      cvc: paymentFormValues!.cvc!,
      zip: paymentFormValues.billingAddressData!.postalCode!,
    });

    try {
      let authorizeNetResponse:
        | AuthorizeAcceptSuite.DispatchDataResponse
        | undefined;

      if (invoice?.total ?? 0 > 0) {
        const authData = new AuthorizeAcceptSuite.AuthData(
          session.society!.societySettingsPublic!.authorizeNetPublicClientId!,
          session.society!.societySettingsPublic!.authorizeNetMerchantNameLoginId!,
        );

        const secureData = new AuthorizeAcceptSuite.SecureData(
          authData,
          formattedCardDetails,
        );

        const paymentProcessor = new AuthorizeAcceptSuite.PaymentProcessor(
          secureData,
        );
        authorizeNetResponse = await paymentProcessor.getResponse();

        if (authorizeNetResponse.messages.resultCode !== "Ok") {
          setCheckoutError(
            authorizeNetResponse.messages.message.map((m) => m.text).join(", "),
          );
          return;
        }

        paymentFormValues.billingAddressData!.postalCode =
          formattedCardDetails.zip ?? "";
        paymentFormValues.shippingAddressData!.postalCode =
          formattedCardDetails.zip ?? "";
      }

      await apiRequestContractHandler(
        postSocietyProfileAccountingInvoicePaymentContract,
        {
          params: {
            societyId: session.societyId!,
            profileId: session.profileId!,
            invoiceId: Number(invoice?.invoiceId),
          },
          body: {
            amount: invoice?.total ?? 0,
            shippingAddress: paymentFormValues!.isSameAsBilling
              ? paymentFormValues.billingAddressData
              : paymentFormValues.shippingAddressData,
            billingAddress: paymentFormValues.billingAddressData!,
            authorizeAcceptSuite: authorizeNetResponse,
          },
        },
      );

      const conferenceQueryKeys = [];
      if (
        invoice?.workflowInstance?.conferenceRegistration?.conference
          ?.conferenceId
      ) {
        conferenceQueryKeys.push(
          queryClient.invalidateQueries({
            queryKey: [
              "conferenceRegistration",
              invoice?.workflowInstance?.conferenceRegistration?.conference
                ?.conferenceId,
              session.profile?.profileId,
            ],
          }),
        );
      }
      invoice?.invoiceItems?.forEach((item) => {
        if (
          item.workflowInstance?.conferenceRegistration?.conference
            ?.conferenceId
        ) {
          conferenceQueryKeys.push(
            queryClient.invalidateQueries({
              queryKey: [
                "conferenceRegistration",
                invoice?.workflowInstance?.conferenceRegistration?.conference
                  ?.conferenceId,
                session.profile?.profileId,
              ],
            }),
          );
        }
      });

      await Promise.all([
        session.refreshSession(),
        queryClient.invalidateQueries([
          `${allInvoicesQueryKey}-${session.profileId}`,
        ]),
        queryClient.invalidateQueries([
          `${unPaidInvoicesQueryKey}-${session.profileId}`,
        ]),
        queryClient.invalidateQueries(["pillarTable", invoicesTableKey]),
        queryClient.invalidateQueries([`invoiceId-${invoice?.invoiceId}`]),
        ...conferenceQueryKeys,
      ]);

      router.replace("/overview");
    } catch (error: any) {
      setCheckoutError(error?.message);
    }
  };

  return (
    <fieldset disabled={invoiceIsLoading}>
      <PillarForm
        handleSubmit={handleSubmit}
        handleChanged={setPaymentFormValues}
        yupValidation={
          invoice?.total && invoice.total > 0
            ? paymentFormvalidationSchema
            : undefined
        }
        validateOnChange={false}
        overrideInitialValues={{
          fullName: getDisplayNameHelper(session.profile!) ?? "",
          billingAddressData: {
            line1: billingAddress?.line1 ?? "",
            city: billingAddress?.city ?? "",
            state: billingAddress?.state ?? "",
            country: billingAddress?.country ?? "",
            postalCode: billingAddress?.postalCode ?? "",
          },
          shippingAddressData: {
            line1: shippingAddress?.line1 ?? "",
            city: shippingAddress?.city ?? "",
            state: shippingAddress?.state ?? "",
            country: shippingAddress?.country ?? "",
            postalCode: billingAddress?.postalCode ?? "",
          },
        }}
      >
        <PillarForm.SubmittingModal testid="payment-processing-modal">
          <TemplateComponent
            testid="payment-processing-template"
            data={{ invoice, session }}
            template={
              routeConfig.routeParams?.paymentProcessingModalTemplate ??
              "<p>Please wait while your payment is being processed. Do not close this window. You will be redirected when your payment is complete.</p>"
            }
          />
        </PillarForm.SubmittingModal>
        <div className="w-full">
          {(invoice?.total ?? 0) > 0 ? (
            <>
              <HubCheckoutPaymentOptions />
              {paymentType == PaymentType.CARD && (
                <>
                  <div className="py-4">
                    <HubCheckoutAddressForm addressType={"Billing"} />
                  </div>
                  <section aria-labelledby="billing-heading">
                    <HubCheckoutSameAsBillingCheckbox />
                    {!paymentFormValues?.isSameAsBilling && (
                      <div className="pb-6">
                        <HubCheckoutAddressForm addressType={"Shipping"} />
                      </div>
                    )}
                    <HubCheckoutPaymentDetailsForm />
                  </section>

                  <div className="mt-4">
                    {getProfileIsExpired(session) ? (
                      <PillarForm.Button
                        disabled={payButtonDisabled}
                        className="button-regular-general-filled w-full"
                        onClick={() => {
                          router.push(
                            `/edit-user-detail?invoiceId=${invoice!.invoiceId}`,
                          );
                        }}
                      >
                        {
                          session.society?.societySettingsPublic
                            ?.outdatedProfileNavigateToEditText
                        }
                      </PillarForm.Button>
                    ) : (
                      <PillarForm.LoadingSubmitButton
                        disabled={payButtonDisabled}
                        testid="payment-pay-button"
                        className="button-regular-general-filled w-full"
                      >
                        Pay
                      </PillarForm.LoadingSubmitButton>
                    )}
                  </div>

                  {invoice?.status === INVOICE_STATUS.PAID && (
                    <div className="text-center text-xs text-status-success">
                      Paid
                    </div>
                  )}
                </>
              )}
              {paymentType == PaymentType.CHECK && <CheckPaymentForm />}
            </>
          ) : (
            <PillarForm.LoadingSubmitButton
              testid="payment-pay-button"
              className="button-regular-general-filled w-full"
            >
              Confirm $0 Payment
            </PillarForm.LoadingSubmitButton>
          )}
        </div>
      </PillarForm>
    </fieldset>
  );
};

export default HubCheckoutPaymentForm;
